import { Popover, Tooltip } from 'antd';
import Image from 'next/image';
import React, { memo, useEffect, useMemo, useRef, useState } from 'react';

import MoreMenu, { MoreMenuProps } from '@/components/common/MoreMenu';
import { PaymentModal } from '@/components/UIComponents/CommonModal';
import { useTranslate } from '@/hooks/useTranslate';
import { AIFunction } from '@/utils/const';

import {
  ExpiringSoonIcon,
  FailIcon,
  LoadingIcon,
  ReviewFailIcon,
  UpgradeIcon,
  VideoMuteIcon,
  VideoUnmuteIcon,
} from './icon';

export enum AvatarState {
  NORMAL = 2, // 正常
  GENERATING = 1, // 生成中
  FAILED = 3, // 失败
  EXPIRED = 5, // 已过期
  REVIEWFAILED = 6, // 审核失败
}

export type ExpireStateType = 'normal' | 'soon' | 'expired';

interface AvatarItemProps {
  type: 'custom' | 'template';
  itemData: any;
  coverUrl: string;
  videoUrl: string;
  name: string;
  state: AvatarState;
  isPro?: boolean;
  isMute: boolean;
  leftTime?: number;
  expireState?: ExpireStateType;
  onMute: (isMute: boolean) => void;
  onUpgrade: () => void | undefined;
  onUpdated: () => void;
  onSelect?: (avatar: any) => void;
  actions?: MoreMenuProps;
}

const StatusMask: React.FC<{
  state: AvatarState;
  expireTime?: string | number;
  onUpdated: () => void;
}> = ({ state, onUpdated }) => {
  const { t } = useTranslate();
  const statusConfig = {
    [AvatarState.NORMAL]: null,
    [AvatarState.GENERATING]: (
      <div className="flex flex-col items-center justify-center h-full gap-[24px]">
        <LoadingIcon />
        <span className="text-[10px] text-[#ffffff]">{t('merror.tip.generating')}</span>
      </div>
    ),
    [AvatarState.FAILED]: (
      <div className="flex flex-col items-center justify-center h-full gap-[24px]">
        <FailIcon />
        <span className="text-[10px] text-[#DF3F1F]">{t('GenerateFailed')}</span>
      </div>
    ),
    [AvatarState.REVIEWFAILED]: (
      <div className="flex flex-col items-center justify-center h-full gap-[24px]">
        <ReviewFailIcon />
        <span className="text-[10px] text-[#DF3F1F]">{t('Avatar-Review-Failed')}</span>
      </div>
    ),

    [AvatarState.EXPIRED]: (
      <div className="flex flex-col items-center justify-center h-full text-white">
        <div className="w-full h-full flex flex-col justify-center items-center">
          <div className="font-[500] text-[#FDD014] text-[14px] leading-[20px] mb-[8px]">
            {t('Expired')}
          </div>
          <div
            className="cursor-pointer bg-[#5959E5] px-[12px] py-[6px] rounded-[6px] text-[14px]"
            // onClick={() => {
            //   PaymentModal.show({
            //     feature: 'pro_avatar',
            //     autoNext: true,
            //     onOk: () => {
            //       onUpdated();
            //     },
            //   });
            // }}
          >
            {t('Upgrade')}
          </div>
        </div>
      </div>
    ),
  };

  const config = statusConfig[state];

  if (!config) return null;

  return <div className={`absolute inset-0 bg-[#00000080] z-10`}>{config}</div>;
};

const PreviewVideo: React.FC<{
  videoUrl: string;
  isMute: boolean;
  shouldLoad: boolean;
  muteStyle: React.CSSProperties;
  onMute: (isMute: boolean) => void;
}> = memo(({ videoUrl, isMute, shouldLoad, muteStyle, onMute }) => {
  const [isLoading, setIsLoading] = useState(true);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (shouldLoad) {
      setIsLoading(true);
      video.load();
    } else {
      setIsLoading(false);
      video.pause();
      video.currentTime = 0;
      video.src = '';
      video.load();
    }
  }, [shouldLoad]);

  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      video.muted = isMute;
    }
  }, [isMute]);

  const handleVideoLoad = () => {
    const video = videoRef.current;
    if (!video || !shouldLoad) return;

    setIsLoading(false);
    video.muted = isMute;

    if (shouldLoad) {
      video.play().catch((error) => {
        console.warn('Video play failed, trying muted:', error);
        video.muted = true;
        video.play().catch(() => {
          console.warn('Muted play also failed');
        });
      });
    }
  };

  const handleMuteToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onMute(!isMute);
  };

  if (!shouldLoad) return null;

  return (
    <div className="absolute top-0 left-0 w-full h-full object-cover z-[5]">
      <span
        className="absolute z-20 cursor-pointer"
        onClick={handleMuteToggle}
        style={muteStyle}
      >
        {isMute ? <VideoMuteIcon /> : <VideoUnmuteIcon />}
      </span>

      {isLoading && (
        <div className="w-full h-full flex justify-center items-center">
          <LoadingIcon />
        </div>
      )}

      <video
        ref={videoRef}
        className="w-full h-full object-cover"
        style={{ display: isLoading ? 'none' : 'block' }}
        src={shouldLoad ? videoUrl : ''}
        loop
        playsInline
        muted={isMute}
        onLoadedData={handleVideoLoad}
        preload="none"
      />
    </div>
  );
});

const ImageContainer = memo(
  ({ url, style }: { url: string; style: React.CSSProperties }) => {
    const [isLoaded, setIsLoaded] = useState(false);

    return (
      <div className="relative w-full h-full transition-all" style={style}>
        {!isLoaded && <div className="absolute inset-0  animate-pulse" />}

        <Image
          priority
          className={`transition-opacity duration-200 ease-in-out w-[118px] h-[218px]
            ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
          style={{
            userSelect: 'none',
            objectFit: 'cover',
            objectPosition: 'center',
          }}
          fill={true}
          src={url}
          alt="my-creation"
          onLoad={() => setIsLoaded(true)}
        />
      </div>
    );
  },
  (prevProps, nextProps) =>
    prevProps.url === nextProps.url &&
    JSON.stringify(prevProps.style) === JSON.stringify(nextProps.style),
);

const AvatarItem: React.FC<AvatarItemProps> = memo(
  (props) => {
    const {
      type,
      itemData,
      coverUrl,
      videoUrl,
      name,
      state,
      isPro = false,
      isMute,
      leftTime,
      expireState = 'normal',
      onMute,
      onUpgrade,
      onUpdated,
      onSelect,
      actions = [],
    } = props;
    const [shouldLoadVideo, setShouldLoadVideo] = useState(false);
    const hoverTimerRef = useRef<NodeJS.Timeout | null>(null);
    const { t } = useTranslate();

    const handleImageMouseEnter = () => {
      if (hoverTimerRef.current) {
        clearTimeout(hoverTimerRef.current);
        hoverTimerRef.current = null;
      }
      if (state !== AvatarState.NORMAL || !videoUrl) {
        return;
      }
      hoverTimerRef.current = setTimeout(() => {
        setShouldLoadVideo(true);
      }, 500);
    };

    const handleTooltipContentMouseEnter = () => {
      if (hoverTimerRef.current) {
        clearTimeout(hoverTimerRef.current);
        hoverTimerRef.current = null;
      }
      setShouldLoadVideo(false);
    };

    const handleImageMouseLeave = (e: React.MouseEvent) => {
      if (hoverTimerRef.current) {
        clearTimeout(hoverTimerRef.current);
        hoverTimerRef.current = null;
      }
      setShouldLoadVideo(false);
    };

    useEffect(() => {
      return () => {
        if (hoverTimerRef.current) {
          clearTimeout(hoverTimerRef.current);
        }
      };
    }, []);

    const onUse = () => {
      if (type === 'custom') {
        onSelect?.(itemData);
      } else {
        // Template avatar - check if pro
        if (isPro) {
          PaymentModal.show({
            feature: AIFunction.Other,
            autoNext: true,
            customTip: t('avatar-pro-tip'),
            onOk: () => {
              onSelect?.(itemData);
            },
          });
        } else {
          onSelect?.(itemData);
        }
      }
    };

    const expiredSoonTip = useMemo(() => {
      if (!leftTime) return '';
      const oneDayTime = 3600 * 24 * 1000;
      const leftDay = leftTime / oneDayTime;
      const amount =
        leftDay < 1
          ? t('will-expire-in-today')
          : t('This-avatar-will-expire-in-amount-days', { amount: Math.ceil(leftDay) });
      return amount;
    }, [leftTime, t]);

    return (
      <div
        className="w-[120px] h-[218px] rounded-[8px] overflow-hidden group relative  border-2 border-transparent hover:border-[#5959E5] transition-colors shrink-0"
        onMouseEnter={handleImageMouseEnter}
        onMouseLeave={handleImageMouseLeave}
      >
        <div
          className="w-full h-full relative  rounded-[8px] "
          style={{
            background:
              'radial-gradient(50% 71.86% at 50% 50%, rgba(168, 127, 255, 0.04) 0%, rgba(168, 127, 255, 0.00) 100%), rgba(0, 0, 0, 0.33)',
            backdropFilter: 'blur(15.14702320098877px)',
          }}
        >
          {state !== AvatarState.REVIEWFAILED && (
            <ImageContainer
              url={coverUrl}
              style={{ opacity: !videoUrl ? 1 : shouldLoadVideo ? 0 : 1 }}
            />
          )}
          <div className="absolute top-[8px] left-[8px] z-[15]">
            {isPro && (
              <div className="bg-gradient-to-r from-[#FFC700] to-[#FF8A00] px-[6px] py-[2px] rounded-[4px] text-[10px] font-bold text-white">
                PRO
              </div>
            )}
            {expireState === 'soon' && (
              <Tooltip
                rootClassName="avatar-panel-expire !max-w-[350px]"
                title={() => {
                  return (
                    <div
                      className="overflow-hidden w-[auto] h-[44px] bg-[#434343] flex rounded-[8px] p-[10px] gap-[18px] items-center"
                      onMouseEnter={handleTooltipContentMouseEnter}
                    >
                      <div className="text-[12px] text-[#f3f3f3] font-[600] flex-shrink-0">
                        {expiredSoonTip}
                      </div>
                      <div
                        className="cursor-pointer bg-[#030104] px-[12px] py-[4px] rounded-[4px] flex gap-[4px] items-center"
                        onClick={onUpgrade}
                      >
                        <UpgradeIcon />
                        <span className="font-[600] text-[14px] leading-[16px]">
                          {t('Upgrade')}
                        </span>
                      </div>
                    </div>
                  );
                }}
              >
                <div className=" w-[16px] h-[18px] left-[8px] top-[8px]">
                  <ExpiringSoonIcon />
                </div>
              </Tooltip>
            )}
          </div>

          {state === AvatarState.NORMAL && videoUrl && (
            <PreviewVideo
              videoUrl={videoUrl}
              shouldLoad={shouldLoadVideo}
              isMute={isMute}
              muteStyle={{
                right: '10px',
                top: actions.length ? '40px' : '10px',
              }}
              onMute={onMute}
            />
          )}
          <StatusMask state={state} onUpdated={onUpdated} />

          {actions.length ? (
            <div className="absolute top-[6px] right-[6px]  z-[25] opacity-0 group-hover:opacity-100 transition-opacity">
              <MoreMenu menu={actions} />
            </div>
          ) : null}
        </div>
        <div
          className={`absolute h-fit w-full z-[100] bottom-0  transition-all select-none
            ${state === AvatarState.GENERATING || state === AvatarState.FAILED ? ' pointer-events-none' : ''}`}
        >
          <div
            className="absolute bottom-[0px] left-0 w-full h-[34px] flex justify-center items-center
           overflow-hidden text-[#fff] font-[14px]
           group-hover:pointer-events-none z-20 opacity-100 group-hover:opacity-0 transition-opacity"
            style={{
              borderRadius: '0 0 6px 6px',
              background:
                'radial-gradient(50% 71.86% at 50% 50%, rgba(168, 127, 255, 0.04) 0%, rgba(168, 127, 255, 0.00) 100%), rgba(4, 1, 21, 0.10)',
              backdropFilter: 'blur(11.52163028717041px)',
            }}
          >
            <div className="w-[92%] overflow-hidden text-center text-[12px] line-clamp-2 font-[600]  break-words leading-[16px]">
              {name}
            </div>
          </div>
          {state === AvatarState.NORMAL && (
            <div className="w-full h-[30px] flex justify-center">
              <div
                className="w-[96%] left-1/2 -translate-x-1/2 absolute bottom-[2px] h-full rounded-[8px] bg-[#4931FA]
          flex justify-center items-center text-[14px] cursor-pointer text-[#f0f0fa] z-20 opacity-0 group-hover:opacity-100"
                onClick={onUse}
              >
                {t('Use')}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.coverUrl === nextProps.coverUrl &&
      prevProps.videoUrl === nextProps.videoUrl &&
      prevProps.name === nextProps.name &&
      prevProps.state === nextProps.state &&
      prevProps.isPro === nextProps.isPro &&
      prevProps?.leftTime === nextProps?.leftTime &&
      prevProps.expireState === nextProps.expireState &&
      prevProps.isMute === nextProps.isMute &&
      JSON.stringify(prevProps.actions) === JSON.stringify(nextProps.actions) &&
      prevProps.onMute === nextProps.onMute &&
      prevProps.onUpgrade === nextProps.onUpgrade &&
      prevProps.onUpdated === nextProps.onUpdated
    );
  },
);

export default AvatarItem;
